import { useState, useEffect, useCallback } from "react";
import api from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { toast } from "sonner";
import { Clock, Zap, Crown, Loader2, CheckCircle, ShieldCheck, Smartphone, Copy, Sparkles, Hourglass, TrendingUp, Info } from "lucide-react";

const PACK_VISUALS = {
  M: { Icon: Zap, gradient: "from-[#10B981] to-[#059669]", border: "border-[#10B981]", accent: "#10B981", tag: "Essentiel" },
  L: { Icon: Sparkles, gradient: "from-[#3B82F6] to-[#1D4ED8]", border: "border-[#3B82F6]", accent: "#3B82F6", tag: "Populaire" },
  XL: { Icon: Crown, gradient: "from-[#F59E0B] to-[#D97706]", border: "border-[#F59E0B]", accent: "#F59E0B", tag: "Premium" },
};

function formatRemainingDays(expiresAt) {
  try {
    const exp = new Date(expiresAt).getTime();
    const now = Date.now();
    const diffMs = exp - now;
    if (diffMs <= 0) return "Expiré";
    const days = Math.floor(diffMs / 86400000);
    const hours = Math.floor((diffMs % 86400000) / 3600000);
    if (days > 0) return `${days}j ${hours}h restants`;
    const mins = Math.floor((diffMs % 3600000) / 60000);
    return `${hours}h ${mins}m restantes`;
  } catch { return "—"; }
}

export default function PassengerPacks() {
  const [catalog, setCatalog] = useState({ packs: [], validity_days: 3, operators: {} });
  const [activePacks, setActivePacks] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPack, setSelectedPack] = useState(null);
  const [operator, setOperator] = useState("mtn");
  const [reference, setReference] = useState("");
  const [phone, setPhone] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [ussd, setUssd] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cat, active, my] = await Promise.all([
        api.get("/passenger/packs/catalog"),
        api.get("/passenger/packs/active"),
        api.get("/passenger/packs/purchases"),
      ]);
      setCatalog(cat.data);
      setActivePacks(active.data.packs || []);
      setPurchases(my.data || []);
      // Default to first available operator
      const ops = Object.keys(cat.data.operators || {});
      if (ops.length && !ops.includes(operator)) setOperator(ops[0]);
    } catch (e) {
      toast.error("Erreur de chargement", { description: e.response?.data?.detail || "" });
    } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  const openPurchaseDialog = (pack) => {
    setSelectedPack(pack);
    setReference("");
    setPhone("");
    setUssd("");
    const ops = Object.keys(catalog.operators || {});
    if (ops.length) setOperator(ops[0]);
  };

  const generateUssd = () => {
    const op = catalog.operators?.[operator];
    if (!op || !selectedPack) return "";
    return op.ussd_template.replace("{amount}", selectedPack.price_xaf);
  };

  const handleDial = () => {
    const code = generateUssd();
    setUssd(code);
    try { window.location.href = `tel:${encodeURIComponent(code)}`; } catch { /* noop */ }
  };

  const handleCopy = async () => {
    const code = ussd || generateUssd();
    setUssd(code);
    try { await navigator.clipboard.writeText(code); toast.success("Code copié"); } catch { /* noop */ }
  };

  const handleSubmit = async () => {
    if (!reference.trim()) { toast.error("Référence MoMo requise"); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/passenger/packs/purchase", {
        pack_type: selectedPack.type, operator, reference: reference.trim(), phone_number: phone,
      });
      toast.success(data.message);
      setSelectedPack(null);
      await load();
    } catch (e) {
      toast.error("Échec de la demande", { description: e.response?.data?.detail || "Erreur" });
    } finally { setSubmitting(false); }
  };

  const totalHoursRemaining = activePacks.reduce((s, p) => s + Number(p.hours_remaining || 0), 0);

  return (
    <div className="space-y-5" data-testid="passenger-packs-panel" style={{ fontFamily: "Outfit, Inter, sans-serif" }}>
      {/* Hero — Prépayez. Économisez. */}
      <div
        className="relative overflow-hidden rounded-3xl text-white"
        style={{
          backgroundImage:
            "linear-gradient(180deg, rgba(10,37,64,0.85) 0%, rgba(10,37,64,0.95) 100%), url('https://images.unsplash.com/photo-1519641471654-76ce0107ad1b?w=800&fit=crop&q=60')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="p-6 sm:p-8">
          <span className="inline-flex items-center gap-1.5 bg-[#FACC15] text-[#0A2540] text-[10px] uppercase font-extrabold tracking-widest rounded-full px-3 py-1">
            <Hourglass className="w-3 h-3" /> Packs Heures
          </span>
          <h2
            className="mt-4 text-4xl sm:text-5xl font-extrabold leading-[1.1] tracking-tight"
            style={{ fontFamily: "Outfit" }}
          >
            Prépayez.<br />Économisez.
          </h2>
          <p className="mt-3 text-[14px] text-white/85 max-w-md">
            Réservez vos heures à tarif préférentiel — payables en MTN MoMo ou Orange Money.
          </p>
          <div className="mt-4 flex items-baseline gap-2">
            <span className="text-3xl font-extrabold text-[#FACC15]" data-testid="total-pack-hours">
              {totalHoursRemaining}
            </span>
            <span className="text-sm font-semibold text-white/70">heures disponibles</span>
          </div>
        </div>
      </div>

      {/* Active packs */}
      {activePacks.length > 0 && (
        <div className="space-y-3" data-testid="active-packs-list">
          <h3 className="text-sm font-bold text-[#0A2540] uppercase tracking-wide" style={{ fontFamily: 'Outfit' }}>Packs actifs</h3>
          {activePacks.map((p) => {
            const v = PACK_VISUALS[p.type] || PACK_VISUALS.M;
            const pct = Math.max(0, Math.min(100, Math.round((p.hours_remaining / p.hours_total) * 100)));
            return (
              <div key={p.id} className={`bg-white rounded-2xl border-2 ${v.border} p-4`} data-testid={`active-pack-${p.type}`}>
                <div className="flex items-center gap-3">
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${v.gradient} flex items-center justify-center text-white shrink-0`}>
                    <v.Icon className="w-6 h-6" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h4 className="text-base font-bold text-[#0A2540]" style={{ fontFamily: 'Outfit' }}>Pack {p.type}</h4>
                    </div>
                    <p className="text-xs text-[#64748B] mt-0.5"><Clock className="w-3 h-3 inline mr-0.5" /> {formatRemainingDays(p.expires_at)}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-extrabold text-[#0A2540]" style={{ fontFamily: 'Outfit' }} data-testid={`pack-hours-${p.type}`}>{p.hours_remaining}<span className="text-xs text-[#64748B]">/{p.hours_total}h</span></p>
                  </div>
                </div>
                <div className="mt-3 h-1.5 bg-[#F1F5F9] rounded-full overflow-hidden">
                  <div className="h-full transition-all" style={{ width: `${pct}%`, background: v.accent }} />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* How it works */}
      <div className="bg-[#FACC15]/10 border border-[#FACC15]/30 rounded-xl p-4 flex items-start gap-3" data-testid="pack-info">
        <Info className="w-4 h-4 text-[#0A2540] mt-0.5 shrink-0" />
        <p className="text-xs text-[#0A2540] leading-relaxed">
          Lors d&apos;une <strong>location horaire</strong>, votre pack s&apos;applique automatiquement si le véhicule est éligible. La tarification du pack remplace le tarif du véhicule jusqu&apos;à épuisement de vos heures ou expiration du pack.
        </p>
      </div>

      {/* Catalog */}
      <div className="space-y-3">
        <h3 className="text-lg font-bold text-[#0A2540]" style={{ fontFamily: "Outfit" }}>
          Choisissez votre pack
        </h3>
        {loading ? (
          <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 text-[#0A2540] animate-spin" /></div>
        ) : catalog.packs.length === 0 ? (
          <p className="text-sm text-[#64748B]">Aucun pack disponible.</p>
        ) : (
          <div className="space-y-4">
            {catalog.packs.map((p, idx) => {
              const isPopular = idx === 1;
              const isBest = idx === catalog.packs.length - 1 && catalog.packs.length > 1;
              const hourly = p.hours > 0 ? Math.round(p.price_xaf / p.hours) : 0;
              const savings = Math.max(0, (hourly ? p.hours * (catalog.packs[0]?.price_xaf / (catalog.packs[0]?.hours || 1)) - p.price_xaf : 0));
              return (
                <div
                  key={p.type}
                  className={`relative bg-white rounded-3xl overflow-hidden ${
                    isPopular || isBest ? "border-2 border-[#FACC15]" : "border border-[#E2E8F0]"
                  }`}
                  data-testid={`pack-card-${p.type}`}
                >
                  {isPopular && (
                    <div className="absolute -right-10 top-4 rotate-45 bg-[#FACC15] text-[#0A2540] text-[10px] font-extrabold tracking-widest px-10 py-1 shadow">
                      POPULAIRE
                    </div>
                  )}
                  {isBest && (
                    <div className="absolute -right-10 top-4 rotate-45 bg-[#0A2540] text-[#FACC15] text-[10px] font-extrabold tracking-widest px-10 py-1 shadow">
                      MEILLEURE OFFRE
                    </div>
                  )}
                  <div className="p-5">
                    <div className="flex items-start gap-4">
                      <div className="w-16 h-16 rounded-full bg-[#F1F5F9] flex flex-col items-center justify-center shrink-0">
                        <span className="text-xl font-extrabold text-[#0A2540] leading-none" style={{ fontFamily: "Outfit" }}>
                          {p.hours}
                        </span>
                        <span className="text-[9px] font-semibold text-[#64748B] mt-0.5">heures</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="text-lg font-extrabold text-[#0A2540]" style={{ fontFamily: "Outfit" }}>
                          {p.label}
                        </h4>
                        <p className="text-sm text-[#64748B] mt-0.5">
                          {p.hours} heures de location {savings > 0 ? `— économisez ${savings.toLocaleString("fr-FR")} FCFA` : `à utiliser sur ${p.validity_days ?? catalog.validity_days} jours`}
                        </p>
                        <div className="flex flex-wrap gap-2 mt-2">
                          <span className="inline-flex items-center gap-1 bg-[#F1F5F9] text-[#0A2540] text-[11px] font-semibold px-2.5 py-1 rounded-full">
                            🏷 {hourly.toLocaleString("fr-FR")} FCFA/h
                          </span>
                          {savings > 0 && (
                            <span className="inline-flex items-center gap-1 bg-[#DCFCE7] text-[#059669] text-[11px] font-semibold px-2.5 py-1 rounded-full">
                              📉 -{savings.toLocaleString("fr-FR")} FCFA
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="mt-4 pt-4 border-t border-[#F1F5F9] flex items-center justify-between gap-3">
                      <div>
                        <p className="text-[10px] uppercase tracking-widest font-bold text-[#94A3B8]">Prix total</p>
                        <p className="text-2xl font-extrabold text-[#0A2540]" style={{ fontFamily: "Outfit" }}>
                          {p.price_xaf.toLocaleString("fr-FR")}{" "}
                          <span className="text-xs font-semibold text-[#64748B]">FCFA</span>
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => openPurchaseDialog(p)}
                        className="h-12 px-8 rounded-full bg-[#0A2540] text-white font-bold text-sm hover:bg-[#081b2e] transition"
                        data-testid={`buy-pack-${p.type}-btn`}
                      >
                        Acheter
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Recent purchases */}
      {purchases.length > 0 && (
        <div className="bg-white rounded-2xl border border-[#CBD5E1] p-4" data-testid="pack-purchases-history">
          <h3 className="text-sm font-bold text-[#0A2540] uppercase tracking-wide mb-3" style={{ fontFamily: 'Outfit' }}>Historique des achats</h3>
          <div className="space-y-2">
            {purchases.slice(0, 5).map((r) => (
              <div key={r.id} className="flex items-center justify-between p-2 bg-[#F8FAFC] rounded-lg text-xs">
                <div>
                  <p className="font-semibold text-[#0A2540]">{r.pack_type} · {r.operator_label}</p>
                  <p className="text-[10px] text-[#64748B]">Réf: {r.reference}</p>
                </div>
                <span className={`text-[10px] font-bold uppercase px-2 py-1 rounded-full ${r.status === "approved" ? "bg-[#10B981]/10 text-[#10B981]" : r.status === "rejected" ? "bg-[#EF4444]/10 text-[#EF4444]" : "bg-[#FACC15]/20 text-[#0A2540]"}`} data-testid={`purchase-status-${r.id}`}>
                  {r.status === "approved" ? "Approuvé" : r.status === "rejected" ? "Rejeté" : "En attente"}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Purchase dialog */}
      <Dialog open={!!selectedPack} onOpenChange={(o) => !o && setSelectedPack(null)}>
        <DialogContent className="sm:max-w-md" data-testid="purchase-pack-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#0A2540] flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
              <ShieldCheck className="w-4 h-4 text-[#10B981]" /> Achat {selectedPack?.label}
            </DialogTitle>
          </DialogHeader>
          {selectedPack && (
            <div className="space-y-3">
              <div className="bg-gradient-to-br from-[#0A2540] to-[#1E3A5F] rounded-xl p-4 text-white">
                <p className="text-xs text-white/60 uppercase tracking-wide">Montant à payer</p>
                <p className="text-3xl font-extrabold text-[#FACC15]" style={{ fontFamily: 'Outfit' }}>{selectedPack.price_xaf.toLocaleString()} <span className="text-sm">XAF</span></p>
                <p className="text-xs text-white/70 mt-1">→ {selectedPack.hours} heures · valable {selectedPack.validity_days ?? catalog.validity_days} jours</p>
              </div>

              <div>
                <Label className="text-xs text-[#64748B]">Opérateur</Label>
                <div className="grid grid-cols-2 gap-2 mt-1">
                  {Object.entries(catalog.operators).map(([k, o]) => (
                    <button key={k} type="button" onClick={() => setOperator(k)} className={`p-3 rounded-xl border-2 text-left ${operator === k ? "border-[#FACC15] bg-[#FACC15]/10" : "border-[#CBD5E1]"}`} data-testid={`pack-op-${k}`}>
                      <Smartphone className="w-4 h-4 text-[#0A2540] mb-1" />
                      <p className="text-xs font-bold text-[#0A2540]">{o.label}</p>
                    </button>
                  ))}
                </div>
              </div>

              <div className="bg-[#F8FAFC] rounded-lg p-3 space-y-2 border border-[#CBD5E1]">
                <p className="text-[11px] text-[#64748B]">Code USSD à composer :</p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 text-sm font-mono text-[#0A2540] bg-white px-2 py-1.5 rounded border border-[#CBD5E1]" data-testid="pack-ussd-code">{ussd || generateUssd()}</code>
                  <Button size="sm" type="button" onClick={handleCopy} variant="outline" className="h-8 w-8 p-0" title="Copier"><Copy className="w-3.5 h-3.5" /></Button>
                </div>
                <Button onClick={handleDial} className="w-full h-9 bg-[#0A2540] text-white hover:bg-[#081b2e]" data-testid="pack-dial-btn"><Smartphone className="w-3.5 h-3.5 mr-1" /> Composer le code</Button>
              </div>

              <div>
                <Label className="text-xs text-[#64748B]">Référence MoMo (SMS de confirmation)</Label>
                <Input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="Ex: MP260618.1234.A12345" className="h-9 mt-1" data-testid="pack-reference-input" />
              </div>
              <div>
                <Label className="text-xs text-[#64748B]">Numéro utilisé (optionnel)</Label>
                <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+237 6XX XXX XXX" className="h-9 mt-1" data-testid="pack-phone-input" />
              </div>

              <div className="bg-[#FACC15]/10 border border-[#FACC15]/30 rounded-lg p-2 flex items-start gap-2">
                <TrendingUp className="w-3.5 h-3.5 text-[#0A2540] mt-0.5" />
                <p className="text-[11px] text-[#0A2540]">Après validation par l&apos;administrateur (généralement &lt; 1h), le pack sera activé et utilisable immédiatement.</p>
              </div>

              <Button onClick={handleSubmit} disabled={submitting || !reference.trim()} className="w-full h-11 bg-[#FACC15] text-[#0A2540] hover:bg-[#EAB308] font-bold" data-testid="submit-pack-purchase-btn">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <CheckCircle className="w-4 h-4 mr-1" />} Soumettre la demande
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
